import {
    LOCATION_STATE_AU,
    STREET_TYPE_AU,
    type LocationStateCodeAU,
} from '@quipex/shared/contracts';

export interface ParsedAddressFields {
    streetNumber: string;
    streetName: string;
    suburb: string;
    stateCode: LocationStateCodeAU | null;
    postcode: string;
}

const EMPTY_PARSED_ADDRESS: ParsedAddressFields = {
    streetNumber: '',
    streetName: '',
    suburb: '',
    stateCode: null,
    postcode: '',
};

const SORTED_STATES = [...LOCATION_STATE_AU].sort(
    (a, b) => b.name.length - a.name.length
);

const STREET_TYPE_PATTERNS: Array<{ pattern: RegExp; length: number }> =
    Object.entries(STREET_TYPE_AU).flatMap(([short, long]) => {
        const escapedShort = escapeRegex(short);
        const escapedLong = escapeRegex(long);

        return [
            {
                pattern: new RegExp(`\\b${escapedShort}\\b`, 'ig'),
                length: short.length,
            },
            {
                pattern: new RegExp(`\\b${escapedLong}\\b`, 'ig'),
                length: long.length,
            },
        ];
    });

/**
 * Parses an Australian address string into discrete fields.
 * Supports comma and space-separated formats, state codes and full names,
 * and partial addresses.
 *
 * Extraction order: postcode → state → street type boundary → street number
 *
 * @param address - The address string to parse
 * @returns Parsed address fields with empty strings for missing values
 */
export function parseAddressString(address: string): ParsedAddressFields {
    const result: ParsedAddressFields = { ...EMPTY_PARSED_ADDRESS };

    if (!address || typeof address !== 'string') {
        return result;
    }

    let remaining = address.trim();

    // 1. Extract postcode (last 4-digit sequence at end)
    const postcodeMatch = remaining.match(/(\d{4})\s*$/);
    if (postcodeMatch && postcodeMatch.index !== undefined) {
        result.postcode = postcodeMatch[1];
        remaining = remaining.substring(0, postcodeMatch.index).trim();
    }

    // 2. Extract state from end of string only
    const stateExtraction = extractStateFromTail(remaining);
    remaining = stateExtraction.remaining;
    result.stateCode = stateExtraction.stateCode;

    // 3. Split street/suburb
    let streetSegment = remaining;
    let suburbSegment = '';

    if (remaining.includes(',')) {
        const parts = remaining
            .split(',')
            .map((p) => p.trim())
            .filter((p) => p);

        // Preserve "Unit 5, 12A/34 Main Street, Sydney" style inputs.
        const startsWithNumber = (value: string): boolean => /^\d/.test(value);
        const hasUnitPrefix =
            parts.length >= 2 &&
            !startsWithNumber(parts[0]) &&
            startsWithNumber(parts[1]);

        if (hasUnitPrefix) {
            streetSegment = `${parts[0]}, ${parts[1]}`;
            suburbSegment = parts.slice(2).join(', ').trim();
        } else {
            streetSegment = parts[0] ?? '';
            suburbSegment = parts.slice(1).join(', ').trim();
        }
    } else {
        const streetTypeBoundary = findStreetTypeBoundary(remaining);
        if (streetTypeBoundary > 0) {
            streetSegment = remaining.substring(0, streetTypeBoundary).trim();
            suburbSegment = remaining.substring(streetTypeBoundary).trim();
        } else {
            // Last-word heuristic: assume last word is suburb if we have state
            // Otherwise, treat everything as street
            if (result.stateCode) {
                const words = remaining.split(/\s+/).filter((w) => w);
                if (words.length > 1) {
                    suburbSegment = words[words.length - 1] ?? '';
                    streetSegment = words.slice(0, -1).join(' ');
                } else {
                    streetSegment = remaining;
                }
            } else {
                streetSegment = remaining;
            }
        }
    }

    // Handle prefixed unit descriptors, e.g. "Unit 5, 12A/34 Main Street"
    streetSegment = stripUnitPrefix(streetSegment);

    // Normalize suburb
    result.suburb = suburbSegment
        .replace(/^\s*,\s*/, '')
        .replace(/,\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();

    // 4. Extract street number from street segment
    const streetNumberMatch = streetSegment.match(
        /^(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?|-\d+[a-zA-Z]?)?)\s+/
    );
    if (streetNumberMatch) {
        result.streetNumber = streetNumberMatch[1];
        result.streetName = streetSegment
            .substring(streetNumberMatch[0].length)
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();
    } else {
        // No street number found - entire street segment is street name
        result.streetName = streetSegment.replace(/\s+/g, ' ').trim();
    }

    return result;
}

function extractStateFromTail(value: string): {
    stateCode: LocationStateCodeAU | null;
    remaining: string;
} {
    for (const state of SORTED_STATES) {
        const candidates = [state.name, state.code];

        for (const candidate of candidates) {
            const escaped = escapeRegex(candidate).replace(/\s+/g, '\\s+');
            const stateAtEndRegex = new RegExp(
                `(?:^|[\\s,])${escaped}\\s*,?\\s*$`,
                'i'
            );

            if (!stateAtEndRegex.test(value)) {
                continue;
            }

            const remaining = value
                .replace(stateAtEndRegex, '')
                .replace(/,\s*$/, '')
                .replace(/\s+/g, ' ')
                .trim();

            return {
                stateCode: state.code,
                remaining,
            };
        }
    }

    return {
        stateCode: null,
        remaining: value,
    };
}

function stripUnitPrefix(streetSegment: string): string {
    const trimmedStreetSegment = streetSegment.trim();
    const parts = trimmedStreetSegment
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

    if (parts.length < 2) {
        return trimmedStreetSegment;
    }

    const afterFirstComma = parts.slice(1).join(', ');
    if (/^\d/.test(afterFirstComma)) {
        return afterFirstComma;
    }

    return trimmedStreetSegment;
}

function findStreetTypeBoundary(value: string): number {
    const matches: Array<{ index: number; length: number }> = [];

    for (const { pattern, length } of STREET_TYPE_PATTERNS) {
        pattern.lastIndex = 0;
        let matchResult: RegExpExecArray | null;
        while ((matchResult = pattern.exec(value)) !== null) {
            matches.push({ index: matchResult.index, length });
        }
    }

    if (matches.length === 0) {
        return -1;
    }

    matches.sort((a, b) => a.index - b.index);

    // Prefer the last full-word type (e.g. "Street") over shorter
    // abbreviations (e.g. "St"), which commonly appear in suburb names.
    const lastFullWordMatch = [...matches].reverse().find((m) => m.length > 3);
    const selectedMatch = lastFullWordMatch ?? matches[matches.length - 1];

    return selectedMatch.index + selectedMatch.length;
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
